// ModernInstagramPostContent.tsx - FIXED
import React, { useRef } from 'react'
import { Autocomplete, TextField, Grid, Box, IconButton, Typography } from '@mui/material'
import { Instagram, Upload, X, Image as ImageIcon, Video } from 'lucide-react'
import { PostContentProps } from 'components/PostScheduling/PostContent'
import { Platform, useMediaAccountsQuery } from 'graphql/generated/graphql'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import MUICaptionWithEmojis from '../../MUICaptionWithEmojis'

interface MediaFile {
  file: File
  type: 'image' | 'video'
}

function SortableMediaItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = { 
    transform: CSS.Transform.toString(transform), 
    transition, 
    cursor: 'grab',
    opacity: isDragging ? 0.5 : 1,
  }
  
  return (
    <Box sx={{ position: 'relative' }}>
      {/* Drag handle - invisible overlay */}
      <Box
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        sx={{
          ...style,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        }}
      />
      {/* Actual content */}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        {children}
      </Box>
    </Box>
  )
}

export default function ModernInstagramPostContent({
  imageFiles,
  setImageFiles,
  videoFiles,
  setVideoFiles,
  setProfileValue,
  captionRef,
}: PostContentProps): JSX.Element {
  const { data, loading } = useMediaAccountsQuery({
    variables: { platform: Platform.Instagram },
  })

  const combinedFiles: MediaFile[] = [
    ...imageFiles.map((f) => ({ file: f, type: 'image' as const })),
    ...videoFiles.map((f) => ({ file: f, type: 'video' as const })),
  ]

  const setCombinedFiles = (newList: MediaFile[]) => {
    setImageFiles(newList.filter((m) => m.type === 'image').map((m) => m.file))
    setVideoFiles(newList.filter((m) => m.type === 'video').map((m) => m.file))
  }

  const isCarousel = combinedFiles.length > 1
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Requires 8px movement before drag starts
      },
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = combinedFiles.findIndex((m) => `${m.file.name}-${m.file.lastModified}` === active.id)
    const newIndex = combinedFiles.findIndex((m) => `${m.file.name}-${m.file.lastModified}` === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    setCombinedFiles(arrayMove(combinedFiles, oldIndex, newIndex))
  }

  const handleRemove = (index: number) => {
    const newList = [...combinedFiles]
    newList.splice(index, 1)
    setCombinedFiles(newList)
  }

  const handleFiles = (
    newFiles: FileList | null,
    maxFiles = 10,
    maxFileSizeMB = 100
  ) => {
    if (!newFiles) return
    const validFiles: MediaFile[] = Array.from(newFiles)
      .filter((file) => {
        const isImage = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
        const isVideo = ['video/mp4', 'video/mov', 'video/quicktime'].includes(file.type)
        
        if (!isImage && !isVideo) {
          alert(`Unsupported file type: ${file.type}`)
          return false
        }
        if (file.size > maxFileSizeMB * 1024 * 1024) {
          alert(`${file.name} exceeds ${maxFileSizeMB}MB`)
          return false
        }
        return true
      })
      .map((f) => ({ 
        file: f, 
        type: f.type.startsWith('video') ? 'video' : 'image' 
      }))

    if (combinedFiles.length + validFiles.length > maxFiles) {
      alert(`Max ${maxFiles} files allowed.`)
      return
    }

    setCombinedFiles([...combinedFiles, ...validFiles])
  }

  const inputRef = useRef<HTMLInputElement | null>(null)

  return (
    <Box sx={{ color: '#fff' }}>
      {/* Platform Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Instagram size={24} color="#fff" />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 600, color: '#fff' }}>Instagram Post</Typography>
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
            Share your moments with the world
          </Typography>
        </Box>
      </Box>

      {/* Profile Section */}
      <Box sx={{ mb: 3, p: 3, borderRadius: 2, background: 'rgba(30,41,59,0.4)', border: '1px solid rgba(168,85,247,0.15)' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2, color: '#fff' }}>Select Account</Typography>
        <Autocomplete
          id="instagram-account-select"
          loading={loading}
          options={data?.mediaAccounts ?? []}
          getOptionLabel={(option) => option?.accountName ?? ''}
          onChange={(e, value) => value && setProfileValue(value)}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Choose your Instagram account"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(168, 85, 247, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                  backgroundColor: 'rgba(15, 23, 42, 0.5)',
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputBase-input': { color: '#fff' },
              }}
            />
          )}
          sx={{
            '& .MuiAutocomplete-endAdornment': {
              '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
            },
          }}
        />
        {data?.mediaAccounts.length === 0 && !loading && (
          <Box sx={{ mt: 2, p: 1.5, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 1, color: '#fca5a5', fontSize: 14 }}>
            ⚠️ Please connect your Instagram business account first.
          </Box>
        )}
      </Box>

      {/* Upload / Media Section */}
      <Box
        onClick={() => inputRef.current?.click()}
        sx={{
          border: '2px dashed rgba(168,85,247,0.4)',
          borderRadius: 3,
          p: 3,
          textAlign: 'center',
          mb: 3,
          cursor: 'pointer',
          background: 'rgba(15, 23, 42, 0.5)',
          '&:hover': {
            borderColor: 'rgba(168, 85, 247, 0.7)',
            background: 'rgba(31, 41, 55, 0.6)',
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={(e) => handleFiles(e.target.files)}
          style={{ display: 'none' }}
        />
        <Upload size={28} color="#a855f7" />
        <Typography sx={{ color: '#a855f7', mt: 1, fontWeight: 500 }}>
          Upload Photos & Videos
        </Typography>
        {isCarousel && (
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mt: 0.5 }}>
            📱 Carousel post with {combinedFiles.length} items
          </Typography>
        )}

        {combinedFiles.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext 
              items={combinedFiles.map((m) => `${m.file.name}-${m.file.lastModified}`)} 
              strategy={verticalListSortingStrategy}
            >
              <Grid container spacing={1.5} sx={{ mt: 2, justifyContent: 'center' }}>
                {combinedFiles.map((media, index) => (
                  <Grid item key={`${media.file.name}-${media.file.lastModified}`}>
                    <SortableMediaItem id={`${media.file.name}-${media.file.lastModified}`}>
                      <Box
                        sx={{
                          position: 'relative',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '2px solid rgba(168,85,247,0.3)',
                          width: 120,
                          height: 120,
                        }}
                      >
                        {media.type === 'image' ? (
                          <Box
                            component="img"
                            src={URL.createObjectURL(media.file)}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Box
                            component="video"
                            src={URL.createObjectURL(media.file)}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            muted
                            loop
                          />
                        )}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            background: 'rgba(0,0,0,0.7)',
                            color: '#fff',
                            px: 1,
                            borderRadius: 1,
                            fontSize: 12,
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            zIndex: 3,
                          }}
                        >
                          {index + 1}
                          {media.type === 'image' ? <ImageIcon size={12} /> : <Video size={12} />}
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleRemove(index)
                          }}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            background: 'rgba(0,0,0,0.7)',
                            '&:hover': { 
                              background: 'rgba(239, 68, 68, 0.9)',
                              transform: 'scale(1.1)',
                            },
                            width: 28,
                            height: 28,
                            zIndex: 10,
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <X size={16} color="#fff" />
                        </IconButton>
                      </Box>
                    </SortableMediaItem>
                  </Grid>
                ))}
              </Grid>
            </SortableContext>
          </DndContext>
        )}
      </Box>

        {/* Caption with Emoji Support */}
        <MUICaptionWithEmojis
          captionRef={captionRef}
          placeholder="Write your caption... Don't forget the hashtags! #Instagram"
          maxLength={2200}
          rows={5}
        />
    </Box>
  )
}